import { Dropdown } from "@/crud/form/Dropdown";
import yup from "@/crud/yup-extended";
import { useAccreditationStore } from "@/store/accreditation";
import { Dialog, Transition } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { Formik, FormikValues } from "formik";
import {
  BanknoteIcon,
  CreditCardIcon,
  RefreshCcw,
  TicketIcon,
} from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import { RequestPaymentDetails } from "./RequestPaymentDetails";
import {
  AccreditationGroup,
  AccreditationIndividual,
} from "@/models/accreditation";
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import { useState } from "react";
import { set } from "lodash";

interface Props {
  individual: AccreditationIndividual;
}

export const RequestPayment = ({ individual }: Props) => {
  const { requestPaymentIndividual, setRequestPaymentIndividual } =
    useAccreditationStore();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const paymentTypes = [
    {
      id: "ticket",
      value: "Ticket",
    },
    {
      id: "onceOff",
      value: "Once Off",
    },
    {
      id: "subscription",
      value: "Subscription",
    },
  ];

  // Update the validation schema
  const validationSchema = yup.object({
    type: yup
      .object()
      .shape({
        id: yup
          .string()
          .oneOf(
            paymentTypes.map((type) => type.id),
            "Invalid payment type"
          )
          .required("Payment type is required"),
        value: yup
          .string()
          .oneOf(
            paymentTypes.map((type) => type.value),
            "Invalid payment value"
          )
          .required("Payment value is required"),
      })
      .required("Payment type is required"),
    isBond: yup.boolean(),
    tickets: yup
      .array()
      .of(yup.object()) // Define the structure of each ticket if needed
      .when("type.id", {
        is: "ticket",
        then: (schema) => schema.min(1, "At least one ticket is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const mutation = useMutation((formData: FormikValues) =>
    axios.post(
      `${BASE_URL}/accreditation/individuals/${individual.id}/request-payment`,
      formData
    )
  );

  return (
    <Transition.Root show={!!requestPaymentIndividual} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        // initialFocus={cancelButtonRef}
        onClose={setRequestPaymentIndividual}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 dark:bg-dark-secondary dark:bg-opacity-80 backdrop-blur-lg bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex items-end justify-center p-4 text-center h-full items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                id="request-payment-modal"
                className="relative transform rounded-lg bg-white dark:bg-dark-primary px-4 pb-4 pt-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6 "
              >
                <div>
                  <Formik
                    initialValues={{
                      type: null,
                      isBond: false,
                      onlySendReceipt: false,
                      tickets: [],
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (values: FormikValues) => {
                      setIsSubmitting(true);
                      mutation.mutate(values, {
                        onSuccess: () => {
                          console.log(
                            "Payment request submitted successfully!"
                          );
                          setIsSubmitting(false);
                          setRequestPaymentIndividual(undefined);
                          queryClient.invalidateQueries([
                            "individual",
                            individual.id.toString(),
                          ]);
                        },
                        onError: (error) => {
                          console.error(
                            "Error submitting payment request:",
                            error
                          );
                          setIsSubmitting(false);
                        },
                      });
                    }}
                    enableReinitialize
                    validateOnBlur={false}
                    validateOnChange={false}
                  >
                    {({ errors, handleSubmit, values }) => {
                      return (
                        <form>
                          <div className="px-1">
                            <div>
                              <BanknoteIcon className="h-8 w-8 text-zinc-500 dark:text-zinc-400" />
                              <h1 className="text-lg dark:text-white font-semibold mt-1">
                                Request payment from{" "}
                                {requestPaymentIndividual?.firstName}{" "}
                                {requestPaymentIndividual?.lastName}
                              </h1>
                              {/* <p className="dark:text-zinc-500">
                                Create a payment link for{" "}
                                {requestPaymentIndividual.firstName}{" "}
                                {requestPaymentIndividual.lastName}
                              </p> */}
                            </div>

                            <div className="mt-3">
                              <h4 className="dark:text-zinc-500 font-medium text-sm">
                                Select type
                              </h4>

                              <div className="flex mt-2 w-1/3">
                                <Dropdown
                                  name="type"
                                  options={[
                                    {
                                      id: "ticket",
                                      value: "Ticket",
                                    },
                                    {
                                      id: "once-off",
                                      value: "Once Off",
                                    },
                                    {
                                      id: "subscription",
                                      value: "Subscription",
                                    },
                                  ]}
                                  hideSearch
                                />
                                {/* {paymentTypes.map((type, index) => (
                                  <div
                                    key={index}
                                    className="border-2 dark:border-white/20 w-1/5 mr-4 px-4 py-2 rounded-lg"
                                  >
                                    <type.icon className="h-6 w-6 text-zinc-500 dark:text-zinc-500" />
                                    <p className="dark:text-zinc-300 mt-1">
                                      {type.name}
                                    </p>
                                  </div>
                                ))} */}
                              </div>
                            </div>
                          </div>

                          {values.type && (
                            <>
                              {values.type.id === "ticket" ? (
                                <div className="mt-4">
                                  <RequestPaymentDetails
                                    isSubmitting={isSubmitting}
                                    group={individual.group}
                                  />
                                </div>
                              ) : (
                                <div className="text-white mt-4">
                                  <div className="rounded-md bg-blue-500/40 border border-blue-500 p-4">
                                    <div className="flex">
                                      <div className="shrink-0">
                                        <InformationCircleIcon
                                          aria-hidden="true"
                                          className="size-5 text-blue-400"
                                        />
                                      </div>
                                      <div className="ml-3 flex-1 md:flex md:justify-between">
                                        <p className="text-sm text-blue-500">
                                          We are working on adding{" "}
                                          {values.type.value} payments. It will
                                          be available soon, thank you for your
                                          patience. Let your account manager
                                          know if you need this!
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
